import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useState } from 'react';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { useForm } from 'hooks/useForm';
import { createTransaction } from '@redux/features/transactionsSlice';
import { getCashboxes } from '@redux/features/cashboxesSlice';
import { isNotToday } from 'helpers/isNotToday';
import { getTransactionSchema } from 'constants/formValidationSchemas/transactionSchema';
import { transactionsTypesTranslations } from 'constants/transactionTypes';
import { TabSelector } from 'components/TabSelector';
import { Dropdown } from 'components/Dropdown';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import styles from 'pages/CashRegister/CashRegister.scss';

const defaultValues = {
  type: Object.keys(transactionsTypesTranslations)[0],
  description: '',
  amount: '',
  methodID: '',
};

export const CreateTransaction = ({ locationID, cashRegisterID, onTransactionCreated }) => {
  const { paymentMethods } = useSelector((state) => state.paymentMethods);
  const [resetCounter, setResetCounter] = useState(0);
  const query = useSearchParamsUpdater();
  const dispatch = useDispatch();

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, resetForm, setFieldValue } =
    useForm({
      initialValues: defaultValues,
      validationSchema: getTransactionSchema(paymentMethods),
      onSubmit: async (values) => {
        const date = query.getParam('date');

        const result = await dispatch(
          createTransaction({ locationID, cashboxID: cashRegisterID, ...(isNotToday(date) && { date }), ...values })
        );

        if (result.meta.requestStatus === 'fulfilled') {
          await dispatch(getCashboxes(locationID));
          setResetCounter((prev) => prev + 1);
          resetForm();

          const newTransactionId = result.payload?.id;
          setTimeout(() => {
            onTransactionCreated?.(newTransactionId);
          }, 100);
        }
      },
    });

  const handleTypeChange = (index) => {
    const typeKey = Object.keys(transactionsTypesTranslations)[index];
    setFieldValue('type', typeKey);
  };

  const handleMethodChange = (option) => {
    setFieldValue('methodID', option.id);
  };

  const paymentMethodsOptions = useMemo(() => {
    if (!locationID) return paymentMethods;

    return paymentMethods.filter((m) => m.value !== 'cash' || m.cashbox?.location?.id === locationID);
  }, [paymentMethods, locationID]);

  return (
    <div className={styles.section}>
      <h2>Створити нову транзакцію</h2>
      <form className={styles.form} onSubmit={handleSubmit}>
        <TabSelector
          tabs={Object.values(transactionsTypesTranslations)}
          onTabChange={handleTypeChange}
          resetTrigger={resetCounter}
        />
        <Input
          placeholder='Опис'
          name='description'
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={touched.description && errors.description}
          errorText={touched.description && errors.description}
        />
        <Input
          placeholder='Сума'
          name='amount'
          type='number'
          step='100'
          value={values.amount}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={touched.amount && errors.amount}
          errorText={touched.amount && errors.amount}
        />
        <Dropdown
          options={paymentMethodsOptions}
          value={paymentMethods.find((p) => p.id === values.methodID)}
          onChange={handleMethodChange}
          invalid={touched.methodID && errors.methodID}
          errorText={touched.methodID && errors.methodID}
        />
        <div className={styles.buttons}>
          <Button
            text='Скинути'
            type='reset'
            onClick={() => {
              resetForm();
              setResetCounter((prev) => prev + 1);
            }}
            ghostStyle
            smallStyle
          />
          <Button text='Створити' type='submit' disabled={isSubmitting} smallStyle />
        </div>
      </form>
    </div>
  );
};
